import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi } from '../api/endpoints/auth';
import type { AuthUser } from '../types/common';

interface AuthContextType {
  user: AuthUser | null;
  permissions: string[];
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPerm: (key: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await authApi.me();
      if (data.authenticated && data.user) {
        setUser(data.user);
        setPermissions(data.permissions || []);
      } else {
        setUser(null);
        setPermissions([]);
      }
    } catch {
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (username: string, password: string) => {
    const data = await authApi.login(username, password);
    if (data.success && data.user) {
      setUser(data.user);
      setPermissions(data.permissions || []);
    } else {
      throw new Error(data.error || 'Login failed');
    }
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
    setPermissions([]);
  };

  const hasPerm = useCallback((key: string) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return permissions.includes(key);
  }, [user, permissions]);

  return (
    <AuthContext.Provider value={{ user, permissions, loading, login, logout, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
